import { LiteralUnion } from 'models';

import { ObjToURLSearchParams } from 'utils';
import { API_URL } from 'config';
import { makeAPICall } from './api';

import { Antenna, mapAPIToAntenna, API as AntennaAPI } from './antennas';
import { Orbit, mapAPIToOrbit, API as OrbitAPI } from './orbits';
import {
    SchedulePlanOverview,
    mapAPIToSchedulePlanOverview,
    API as SchedulePlanAPI,
} from './scheduleplans';

export type Status = LiteralUnion<
    'auto-booked' | 'booked' | 'archived' | 'skipped' | 'to_confirm' | 'error'
>;

export type CoordinateECEF = [number, number, number];

export declare namespace API {
    export interface PassDetails {
        gs_pos: CoordinateECEF;

        aos_pos: CoordinateECEF;
        aos_vel: CoordinateECEF;
        aos_azimuth: number;
        aos_elevation: number;
        aos_time: number;
        aos_slant_range: number;

        highest_pos: CoordinateECEF;
        highest_vel: CoordinateECEF;
        highest_time: number;
        highest_slant_range: number;

        los_pos: CoordinateECEF;
        los_vel: CoordinateECEF;
        los_azimuth: number;
        los_elevation: number;
        los_time: number;
        los_slant_range: number;

        sun_azimuth: number;
        sun_elevation: number;

        tle_line0: string;
        tle_line1: string;
        tle_line2: string;
        tle_epoch: string;
    }

    export interface Log {
        log: string;
        level: number;
        created: string;
    }

    export interface Schedule {
        id: number;
        start_time: string;
        stop_time: string;
        max_elevation: number;
        min_elevation: number;
        start_azimuth: number;
        final_azimuth: number;
        duration: number;
        pass_details?: PassDetails;
        status: Status;
        created: string;
        observed: boolean;
        locked: boolean;
        orbit?: OrbitAPI.Orbit;
        antenna: AntennaAPI.Antenna;
        scheduleplan: SchedulePlanAPI.SchedulePlanOverview;
        satellite_name: string | null;
        groundstation_name: string | null;
        logs: Log[];
    }

    export interface ScheduleSummary {
        id: number;
        start_time: string;
        stop_time: string;
        max_elevation: number;
        min_elevation: number;
        start_azimuth: number;
        final_azimuth: number;
        duration: number;
        status: Status;
        created: string;
        observed: boolean;
        locked: boolean;
        orbit?: number;
        antenna: number;
        scheduleplan: number;
        satellite_name: string | null;
        groundstation_name: string | null;
    }
}

export interface PassDetails {
    gs_pos: CoordinateECEF;

    aos_pos: CoordinateECEF;
    aos_vel: CoordinateECEF;
    aos_azimuth: number;
    aos_elevation: number;
    aos_time: Date;
    aos_slant_range: number;

    highest_pos: CoordinateECEF;
    highest_vel: CoordinateECEF;
    highest_time: Date;
    highest_slant_range: number;

    los_pos: CoordinateECEF;
    los_vel: CoordinateECEF;
    los_azimuth: number;
    los_elevation: number;
    los_time: Date;
    los_slant_range: number;

    sun_azimuth: number;
    sun_elevation: number;

    tle_line0: string;
    tle_line1: string;
    tle_line2: string;
    tle_epoch: Date;
}

export interface Log {
    log: string;
    level: 'debug' | 'info' | 'warning' | 'error';
    created: Date;
}

export interface Schedule {
    id: number;
    start_time: Date;
    stop_time: Date;
    max_elevation: number;
    min_elevation: number;
    start_azimuth: number;
    final_azimuth: number;
    pass_details?: PassDetails;
    duration: number;
    status: Status;
    created: Date;
    observed: boolean;
    locked: boolean;
    orbit?: Orbit;
    antenna: Antenna;
    scheduleplan: SchedulePlanOverview;
    satelliteName: string | null;
    groundstationName: string | null;
    logs: Log[];
}

export interface ScheduleSummary {
    id: number;
    start_time: Date;
    stop_time: Date;
    max_elevation: number;
    min_elevation: number;
    start_azimuth: number;
    final_azimuth: number;
    duration: number;
    status: Status;
    created: Date;
    observed: boolean;
    locked: boolean;
    orbit?: number;
    antenna: number;
    scheduleplan: number;
    satelliteName: string | null;
    groundstationName: string | null;
}

export interface Step {
    /**
     * The Azumith for this step
     */
    az: number;
    /**
     * The Elevation for this step
     */
    el: number;
    /**
     * The seconds since Epoch to execute going to the procided Azimuth and Elevation
     */
    time: number;
}

export interface Filters {
    limit?: number;
    antenna?: number;
    antenna__groundstation?: number;
    antenna__groundstation__organisation?: number;
    orbit?: number;
    orbit__satellite?: number;
    orbit__satellite__organisation?: number;
    start_time?: string;
    stop_time?: string;
    status?: string;
    status_ne?: string;
    before_start_time?: string;
    before_stop_time?: string;
    after_start_time?: string;
    after_stop_time?: string;
    ordering?: LiteralUnion<keyof ScheduleSummary>;
}

const SCHEDULES_ROUTE = '/schedules';

const SCHEDULES_SUMMARY_ROUTE = '/schedules-overview';

export const mapAPItoScheduleLog = ({ created, level, log }: API.Log): Log => ({
    log,
    created: new Date(created),
    level:
        level == 0
            ? 'info'
            : level == 1
            ? 'warning'
            : level > 2
            ? 'error'
            : 'debug',
});

export const mapAPIToSchedule = ({
    created,
    start_time,
    stop_time,
    antenna,
    orbit,
    scheduleplan,
    pass_details,
    groundstation_name,
    satellite_name,
    logs,
    ...rest
}: API.Schedule): Schedule => ({
    ...rest,
    antenna: mapAPIToAntenna(antenna),
    orbit: orbit && mapAPIToOrbit(orbit),
    scheduleplan: mapAPIToSchedulePlanOverview(scheduleplan),
    pass_details: pass_details
        ? {
              ...pass_details,
              aos_time: new Date(pass_details.aos_time),
              highest_time: new Date(pass_details.highest_time),
              los_time: new Date(pass_details.los_time),
              tle_epoch: new Date(pass_details.tle_epoch),
          }
        : undefined,
    start_time: new Date(start_time),
    stop_time: new Date(stop_time),
    created: new Date(created),
    groundstationName: groundstation_name ?? '',
    satelliteName: satellite_name ?? '',
    logs: logs.map(mapAPItoScheduleLog),
});

export const mapAPIToScheduleSummary = ({
    created,
    start_time,
    stop_time,
    groundstation_name,
    satellite_name,
    ...rest
}: API.ScheduleSummary): ScheduleSummary => ({
    ...rest,
    start_time: new Date(start_time),
    stop_time: new Date(stop_time),
    created: new Date(created),
    groundstationName: groundstation_name ?? '',
    satelliteName: satellite_name ?? '',
});

export const listSchedules = makeAPICall<
    Schedule[],
    Filters,
    { results: API.Schedule[] }
>(
    (filters) => ({
        url: `${API_URL}${SCHEDULES_ROUTE}/?${ObjToURLSearchParams(
            filters,
        ).toString()}`,
    }),
    (data) => data.results.map(mapAPIToSchedule),
);

export const getSchedule = makeAPICall<Schedule, number, API.Schedule>(
    (id) => ({
        url: `${API_URL}${SCHEDULES_ROUTE}/${id}/`,
    }),
    (data) => mapAPIToSchedule(data),
);

export const listScheduleSummeries = makeAPICall<
    ScheduleSummary[],
    Filters,
    { results: API.ScheduleSummary[] }
>(
    (filters) => ({
        url: `${API_URL}${SCHEDULES_SUMMARY_ROUTE}/?${ObjToURLSearchParams(
            filters,
        ).toString()}`,
    }),
    (data) => data.results.map(mapAPIToScheduleSummary),
);

export const updateScheduleStatus = makeAPICall<
    Schedule,
    { id: number; status: string },
    API.Schedule
>(
    ({ id, status }) => ({
        url: `${API_URL}${SCHEDULES_ROUTE}/${id}/`,
        requestData: { body: JSON.stringify({ id, status }), method: 'PATCH' },
    }),
    (data) => mapAPIToSchedule(data),
);
